import { formatJobType } from '@/features/jobs/lib/format-job-type'
import type { JobsReportBaseFilters } from '@/features/reports/types/jobs-report'

export type JobsReportFilterLabels = {
  raisedByUsers: ReadonlyMap<number, string>
  completedByUsers: ReadonlyMap<number, string>
  partners: ReadonlyMap<number, string>
}

const dateLabelFormatter = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const formatFilterDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) {
    return value
  }

  return dateLabelFormatter.format(new Date(year, month - 1, day))
}

const labelForId = (
  id: number,
  labels: ReadonlyMap<number, string>,
  fallbackPrefix: string
) => labels.get(id) ?? `${fallbackPrefix} ${id}`

export const formatJobsReportFilterTitle = (
  filters: JobsReportBaseFilters,
  labels: JobsReportFilterLabels,
  primaryDimensionLabel?: string
) => {
  const parts: string[] = []

  const completedFrom = filters.completed_at_from.trim()
  const completedTo = filters.completed_at_to.trim()

  if (completedFrom && completedTo) {
    parts.push(
      `Outcome completed ${formatFilterDate(completedFrom)} – ${formatFilterDate(completedTo)}`
    )
  } else if (completedFrom) {
    parts.push(`Outcome completed from ${formatFilterDate(completedFrom)}`)
  } else if (completedTo) {
    parts.push(`Outcome completed to ${formatFilterDate(completedTo)}`)
  }

  if (filters.assigned_partner_id) {
    parts.push(
      `SiteWorks partner: ${labelForId(
        filters.assigned_partner_id,
        labels.partners,
        'partner'
      )}`
    )
  }

  if (filters.completed_by_user_id) {
    parts.push(
      `Completed by ${labelForId(
        filters.completed_by_user_id,
        labels.completedByUsers,
        'user'
      )}`
    )
  }

  if (primaryDimensionLabel) {
    parts.push(primaryDimensionLabel)
  }

  const dateRaised = filters.date_raised.trim()
  if (dateRaised) {
    parts.push(`Date raised ${formatFilterDate(dateRaised)}`)
  }

  if (filters.raised_by_user_id) {
    parts.push(
      `Raised by ${labelForId(
        filters.raised_by_user_id,
        labels.raisedByUsers,
        'user'
      )}`
    )
  }

  if (filters.job_type) {
    parts.push(`Job type: ${formatJobType(filters.job_type)}`)
  }

  const location = filters.location.trim()
  if (location) {
    parts.push(`Location: ${location}`)
  }

  return parts.join(' · ')
}
